/**
 * @fileoverview Project one work-comparison contract into timelines, daily grids, calendars, and inspectors. `WorkComparison.js` owns workspace filters; `workComparisonData.js` owns timezone and availability semantics. Detail requests stay scoped to selected people/days. Matter grouping uses explicit recorded links; unavailable source data remains distinct from empty records. Presentation layouts do not merge unrelated projects by similar names.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import api from '../../api';
import { SOURCES, comparisonDays, dayKey, formatTime, isAvailable, sourceState } from './workComparisonData';
import './WorkComparisonLayouts.css';

export const LAYOUTS = [
    { id: 'weekly', label: 'Weekly table' }, { id: 'daily', label: 'Daily grid' },
    { id: 'timeline', label: 'Parallel timelines' }, { id: 'cards', label: 'Employee cards' },
    { id: 'trends', label: 'Totals & trends' }, { id: 'calendar', label: 'Calendar' },
    { id: 'matters', label: 'Matters & projects' }, { id: 'weeks', label: 'Compare weeks' },
    { id: 'feed', label: 'Record feed' }, { id: 'panels', label: 'Comparison panels' },
];


const dateLabel = (date) => moment.utc(date).format('ddd, MMM D');


const stamp = (at, zone) => moment(at).tz(zone).format('MMM D, h:mm:ss a z');


const shiftDate = (date, days) => moment.utc(date).add(days, 'days').format('YYYY-MM-DD');


const sourceName = (key) => SOURCES.find(s => s.key === key)?.label;

/**
 * Clockify totals measure recorded duration; other sources count records. A missing/invalid duration yields unknown rather than zero so layouts cannot present incomplete timing as no work.
 */
export function sourceTotal(source, records) {
    if (source !== 'clockify') return records.length;
    return records.some(r => !Number.isFinite(r.duration_seconds) || r.duration_seconds < 0) ? null : records.reduce((sum, r) => sum + r.duration_seconds, 0);
}



export function buildPeople(data) {
    const future = data?.start > dayKey(new Date(), data?.timezone || 'UTC');
    return (data?.team || []).map(person => ({ ...person,
        sources: future ? Object.fromEntries(SOURCES.map(s => [s.key, { ...person.sources[s.key], status: 'future', value: null }])) : person.sources,
        days: comparisonDays({ ...data, sources: person.sources }),
    }));
}



export function allRecords(people) {
    return people.flatMap(person => SOURCES.flatMap(source => (person.sources[source.key]?.records || []).map(record => ({
        ...record, source: source.key, personId: person.id, personName: person.name,
        key: `${person.id}:${source.key}:${record.id}`,
    }))));
}

// Allocate overlapping intervals to separate lanes; no event is dropped or merged.
/**
 * Position intervals within the selected timezone's actual day boundaries. Day length can vary with timezone transitions; the layout must not assume a fixed 24-hour duration.
 */
export function placeIntervals(records, day, zone, labeledEvents = false) {
    const start = moment.tz(day, zone).startOf('day').valueOf();
    const end = moment.tz(day, zone).startOf('day').add(1, 'day').valueOf();
    const lengths = end - start;
    const items = records.flatMap(

    record => {
        const at = new Date(record.at).getTime();
        const finish = record.source === 'clockify' && record.end ? new Date(record.end).getTime() : at;
        if (!Number.isFinite(at) || !Number.isFinite(finish) || at >= end || finish < start || (finish === start && at < start)) return [];
        const left = Math.max(start, at);
        const right = Math.min(end, Math.max(at, finish));
        return [{ record, from: (left - start) / lengths, to: (right - start) / lengths }];
    }).sort((a, b) => a.from - b.from || a.to - b.to);
    const laneEnds = [];
    items.forEach(

    item => {
        let lane = laneEnds.findIndex(endAt => endAt <= item.from);
        if (lane < 0) lane = laneEnds.length;
        laneEnds[lane] = Math.max(item.to, item.from + (labeledEvents && item.record.source !== 'clockify' ? 0.16 : 0.018));
        item.lane = lane;
    });
    return { items, lanes: Math.max(1, laneEnds.length), start, end };
}



export function groupMatters(records) {
    const groups = new Map();
    records.forEach(

    record => {
        const matter = record.matter_id || record.matter;
        const key = matter ? `matter:${matter}` : record.source === 'clockify' && record.project_id ? `project:${record.project_id}` : `unlinked:${record.source}`;
        const label = matter ? `Matter ${matter}` : record.source === 'clockify' && record.project_id ? `Clockify project · ${record.title}` : `${sourceName(record.source)} · no matter link`;
        if (!groups.has(key)) groups.set(key, { key, label, records: [] });
        groups.get(key).records.push(record);
    });
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}



export function Metric({ source, state, value = state?.value }) {
    const unavailable = !isAvailable(state);
    const label = sourceState(state);
    if (unavailable) return <span className={`wl-state wl-state--${state?.status || 'unavailable'}`} title={label} aria-label={label}>{state?.status === 'future' ? ' ' : state?.status === 'unmapped' ? 'Not linked' : '—'}</span>;
    const partial = state?.status === 'partial';
    return <span className={`wl-metric wl-${source}${partial ? ' is-partial' : ''}`} title={partial ? 'Incomplete history' : undefined}>
        <strong title={source === 'clockify' ? formatTime(value, true) : undefined}>{value == null ? partial ? '—' : 'Duration unavailable' : source === 'clockify' ? formatTime(value) : value}</strong>
        {partial && <span className="wl-partial-mark" aria-label="Partial records">◐</span>}
    </span>;
}



export function Totals({ person }) {
    return <div className="wl-totals">{SOURCES.map(s => <div key={s.key}><span className={`wl-${s.key}`}>{s.label}</span><Metric source={s.key} state={person.sources[s.key]} /></div>)}</div>;
}



function Spark({ person, source, maximum, onDay }) {
    return <div className={`wl-spark wl-${source}`}>
        {person.days.map(

        day => {
            const state = day.sources[source];
            const plotted = isAvailable(state) && state.value != null;
            const title = day.isFuture ? 'Future' : isAvailable(state) ? state.value == null ? 'Incomplete history' : source === 'clockify' ? formatTime(state.value, true) : `${state.value} ${SOURCES.find(s => s.key === source).unit}` : sourceState(state);
            return <button type="button" key={day.key} className={`wl-spark-day${day.isToday ? ' is-today' : ''}`} disabled={day.isFuture || !isAvailable(state)} onClick={() => onDay(person, day.key)} aria-label={`${person.name}, ${sourceName(source)}, ${dateLabel(day.key)}: ${title}`} title={`${dateLabel(day.key)} · ${title}`}>
                <span className={`wl-spark-plot${!plotted ? ' wl-state--' + state.status : ''}`}>
                    {plotted ? state.value === 0 ? <span className="wl-zero">0</span> : <span className={`wl-spark-bar${state.status === 'partial' ? ' is-partial' : ''}`} style={{ height: `${Math.max(3, 100 * state.value / maximum)}%` }} /> : day.isFuture ? null : '—'}
                </span>
                <span className="wl-spark-date">{moment.utc(day.key).format(person.days.length > 7 ? 'D' : 'dd')}</span>
            </button>;
        })}
    </div>;
}



export function RecordInspector({ selection, onClose, zone }) {
    const panel = useRef(null);
    useEffect(

    () => {
        if (!selection) return undefined;
        const previous = document.activeElement;
        panel.current?.focus({ preventScroll: true });
        return () => { if (previous?.isConnected) previous.focus?.({ preventScroll: true }); };
    }, [selection]);
    if (!selection) return null;
    return <section ref={panel} tabIndex={-1} className="wl-inspector" aria-label="Selected records" onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
        <header><h3>{selection.title}</h3><button type="button" onClick={onClose}>Close records ×</button></header>
        {selection.states && <div className="wl-totals">{SOURCES.filter(s => selection.states[s.key]).map(s => <div key={s.key}><span>{s.label}</span><Metric source={s.key} state={selection.states[s.key]} value={selection.states[s.key].value === null ? null : sourceTotal(s.key, selection.records.filter(r => r.source === s.key))} /></div>)}</div>}
        {selection.loading && <p role="status">Loading selected records…</p>}
        {selection.error && <p role="alert">{selection.error}</p>}
        {!selection.loading && !selection.error && !selection.records.length && <p>No stored records for this selection.</p>}
        <ul>{selection.records.map(record => <li key={record.key}>
            <div className={`wl-record-source wl-${record.source}`}>{sourceName(record.source)} · {record.personName}</div>
            <time dateTime={record.at}>{stamp(record.at, zone)}</time>
            <strong>{record.url ? <a href={record.url} target="_blank" rel="noreferrer">{record.title} ↗</a> : record.title}</strong>
            {record.source === 'clockify' && <span>{formatTime(record.duration_seconds, true)} recorded · End: {record.end ? stamp(record.end, zone) : 'not stored'}</span>}
            {record.source === 'trello' && <span>{record.action}</span>}
            {record.source === 'documents' && <span>{record.kind} {record.version}{record.matter ? ` · Matter ${record.matter}` : ''}</span>}
            {record.comparison_url && <a href={record.comparison_url} target="_blank" rel="noreferrer">View changes ↗</a>}
        </li>)}</ul>
    </section>;
}



export function DailyGrid({ people, onDay, selectedKey }) {
    const visiblePeople = people.map(person => ({ ...person, days: person.days.filter(day => !day.isFuture) }));
    const days = visiblePeople[0]?.days || [];
    if (!days.length) return <p>No days to show yet.</p>;
    return <div className="wl-scroll wl-daily-scroll"><table className="wl-daily"><thead><tr><th scope="col">Person / source</th>{days.map(d => <th scope="col" key={d.key} className={d.isToday ? 'is-today' : ''}><span>{moment.utc(d.key).format('ddd')}</span><small>{moment.utc(d.key).format('MMM D')}{d.isToday ? ' · Today' : ''}</small></th>)}</tr></thead>
        {visiblePeople.map(person => <tbody key={person.id} aria-label={person.name}>
            <tr className="wl-daily-person"><th scope="rowgroup">{person.name}</th>{person.days.map(day => <td key={day.key}><button type="button" className="wl-day-open" onClick={() => onDay(person, day.key)} aria-label={`${person.name}, ${dateLabel(day.key)} records`} aria-pressed={selectedKey === `${person.id}:${day.key}`}>↗</button></td>)}</tr>
            {SOURCES.map(source => <tr key={source.key}><th scope="row" className={`wl-${source.key}`}>{source.label}<small>{source.unit}</small></th>{person.days.map(day => {
                const state = day.sources[source.key];
                return <td key={day.key} className={!isAvailable(state) ? 'is-missing' : ''}><button type="button" disabled={!isAvailable(state)} onClick={() => onDay(person, day.key, source.key)} aria-label={`${person.name}, ${source.label}, ${dateLabel(day.key)}`} aria-pressed={selectedKey === `${person.id}:${day.key}:${source.key}`}><Metric source={source.key} state={state} /></button></td>;
            })}</tr>)}
        </tbody>)}
    </table></div>;
}



export function PeopleSelector({ people, selectedIds, onChange, loading }) {
    const chosen = selectedIds === null ? people.map(p => p.id) : selectedIds;
    const count = people.filter(p => chosen.includes(p.id)).length;
    return <details className="wl-people-picker"><summary>People · {selectedIds === null ? 'All selected' : `${count} selected`}</summary>
        <fieldset disabled={loading}><legend>People</legend>
            <div className="wl-people-actions"><button type="button" onClick={() => onChange(null)}>Select all</button><button type="button" onClick={() => onChange([])}>Clear selection</button></div>
            {people.map(p => <label key={p.id}><input type="checkbox" checked={chosen.includes(p.id)} onChange={() => onChange(chosen.includes(p.id) ? chosen.filter(id => id !== p.id) : [...chosen, p.id])} />{p.name}</label>)}
        </fieldset>
    </details>;
}



function Timelines({ person, data, records, day, onRecord, selectedKey }) {
    const daily = person.days.find(d => d.key === day);
    const rows = records.filter(r => r.personId === person.id);
    const midnight = moment.tz(day, data.timezone).startOf('day');
    const dayEnd = midnight.clone().add(1, 'day');
    return <div className="wl-scroll wl-timeline-scroll"><div className="wl-timeline">
        <div className="wl-timeline-head"><strong>{person.name}</strong><div className="wl-axis">{[0, .25, .5, .75, 1].map(v => <span key={v} style={{ left: `${v * 100}%` }}>{v === 1 ? '24:00' : midnight.clone().add((dayEnd - midnight) * v, 'milliseconds').format('HH:mm')}</span>)}</div></div>
        {SOURCES.map(

        s => {
            const state = daily?.sources[s.key];
            const packed = placeIntervals(rows.filter(r => r.source === s.key), day, data.timezone, true);
            const unavailable = !isAvailable(state);
            return <section key={s.key} className={`wl-track wl-${s.key}`}>
                <div className="wl-track-label"><strong>{s.label}</strong><span><Metric source={s.key} state={state} /> <small>{s.unit}</small></span></div>
                <div className={`wl-track-events${unavailable ? ' wl-state--' + state?.status : ''}`} style={{ height: Math.max(44, packed.lanes * 34 + 6) }}>
                    {unavailable ? <span className="wl-track-empty">{sourceState(state)}</span> : <>
                        {packed.items.map(({ record, from, to, lane }) => <button type="button" key={record.key} className={s.key === 'clockify' ? 'wl-time-block' : 'wl-event-marker'} style={{ left: `${from * 100}%`, width: s.key === 'clockify' ? `${Math.max(.6, (to - from) * 100)}%` : `${Math.min(16, (1 - from) * 100)}%`, top: lane * 34 + 4 }} title={`${stamp(record.at, data.timezone)} · ${record.title}`} aria-label={`${s.label}: ${record.title}, ${stamp(record.at, data.timezone)}`} aria-pressed={selectedKey === record.key} onClick={() => onRecord(record)}>
                            {s.key === 'clockify' ? <><strong>{formatTime(record.duration_seconds)}</strong><span>{record.title}</span></> : <><b aria-hidden="true">{s.key === 'documents' ? '◆' : '●'}</b><span>{s.key === 'documents' ? record.title : record.action || record.title}</span></>}
                        </button>)}
                        {!packed.items.length && <span className="wl-track-empty">{state?.status === 'partial' ? '—' : '0'}</span>}
                    </>}
                </div>
            </section>;
        })}
    </div></div>;
}



function Calendar({ people, records, data, onRecord }) {
    const days = people[0]?.days || comparisonDays(data);
    const ids = people.map(p => p.id);
    const selectedRecords = records.filter(r => ids.includes(r.personId));
    return <>
        <details className="wl-calendar-summary"><summary>{people.length} people · source totals</summary><div className="wl-calendar-status">{people.map(p => <div key={p.id}><strong>{p.name}</strong><Totals person={p} /></div>)}</div></details>
        <p className="wl-note">Start/end blocks and event markers · {data.timezone}. Select an event for exact timestamps and recorded duration. Day length follows daylight saving time.</p>
        <div className="wl-scroll"><div className="wl-calendar" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(190px, 1fr))` }}>{days.map(day => {
            const packed = placeIntervals(selectedRecords, day.key, data.timezone);
            return <section key={day.key} className={day.isFuture ? 'is-future' : ''}><h4>{dateLabel(day.key)}{day.isToday ? ' · Today' : ''}</h4><div className="wl-calendar-day">
                {[0, .25, .5, .75, 1].map(v => <span className="wl-calendar-tick" style={{ top: `${v * 100}%` }} key={v}>{v === 1 ? '24:00' : moment(packed.start + (packed.end - packed.start) * v).tz(data.timezone).format('HH:mm')}</span>)}
                {packed.items.map(({ record, from, to, lane }) => <button type="button" key={record.key} className={`wl-calendar-event wl-${record.source}`} style={{ top: `${from * 100}%`, height: `${Math.max(1.8, (to - from) * 100)}%`, left: `${18 + lane * 82 / packed.lanes}%`, width: `${82 / packed.lanes}%` }} title={`${record.personName} · ${sourceName(record.source)} · ${record.title} · ${stamp(record.at, data.timezone)}`} aria-label={`${record.personName}, ${sourceName(record.source)}, ${record.title}, ${stamp(record.at, data.timezone)}`} onClick={() => onRecord(record)}><strong>{record.personName}</strong><span>{sourceName(record.source)} · {record.title}</span></button>)}
            </div></section>;
        })}</div></div>
    </>;
}



function CompareWeeks({ people, data, onPerson }) {
    const [start, setStart] = useState(() => shiftDate(data.start, -7));
    const [other, setOther] = useState(null);
    const [error, setError] = useState('');
    const length = moment.utc(data.end).diff(moment.utc(data.start), 'days');
    const end = shiftDate(start, length);
    useEffect(

    () => {
        let active = true;
        setOther(null); setError('');
        api.get('/admin/work-comparison', { params: { view: 'team', start, end, timezone: data.timezone, ...(data.role === 'attorneys' ? { role: 'attorneys' } : {}) } }).then(({ data: result }) => { if (active) setOther(result); }).catch(() => { if (active) setError('The comparison period could not be loaded.'); });
        return () => { active = false; };
    }, [start, end, data.timezone, data.role]);
    return <>
        <label className="wl-control">Compare with period starting<input type="date" required value={start} onChange={e => { if (e.target.value) setStart(e.target.value); }} /></label>
        <p className="wl-note">Same number of days in both periods. Values reflect each source’s stored coverage; no change scores are calculated.</p>
        {error ? <p role="alert">{error}</p> : !other ? <p role="status">Loading comparison period…</p> : <div className="wl-scroll"><table className="wl-weeks"><thead><tr><th>Person</th><th>{dateLabel(data.start)} – {dateLabel(data.end)}</th><th>{dateLabel(start)} – {dateLabel(end)}</th></tr></thead><tbody>{people.map(p => {
            const previous = other.team?.find(row => row.id === p.id);
            return <tr key={p.id}><th><button type="button" onClick={() => onPerson(p)}>{p.name}</button></th><td><Totals person={p} /></td><td>{previous ? <Totals person={previous} /> : 'No employee record'}</td></tr>;
        })}</tbody></table></div>}
    </>;
}



function Feed({ records, people, data, onRecord }) {
    const [source, setSource] = useState('all');
    const [day, setDay] = useState('all');
    const [limit, setLimit] = useState(100);
    const filtered = records.filter(r => (source === 'all' || r.source === source) && (day === 'all' || dayKey(r.at, data.timezone) === day)).sort((a, b) => new Date(b.at) - new Date(a.at));
    const set = 

    (setter) => e => { setter(e.target.value); setLimit(100); };
    return <>
        <div className="wl-feed-summary">{SOURCES.map(s => <span className={`wl-${s.key}`} key={s.key}>{sourceName(s.key)} · {records.filter(r => r.source === s.key).length} stored records</span>)}</div>
        <div className="wl-controls"><label>Source<select value={source} onChange={set(setSource)}><option value="all">All sources</option>{SOURCES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}</select></label><label>Day<select value={day} onChange={set(setDay)}><option value="all">All days</option>{people[0]?.days.map(d => <option key={d.key} value={d.key}>{dateLabel(d.key)}</option>)}</select></label></div>
        <p className="wl-note">{filtered.length} stored records · newest first</p>
        <ol className="wl-feed">{filtered.slice(0, limit).map(r => <li key={r.key}><button type="button" onClick={() => onRecord(r)}><time>{stamp(r.at, data.timezone)}</time><span className={`wl-${r.source}`}>{sourceName(r.source)}</span><strong>{r.personName}</strong><span>{r.title}</span><small>{r.source === 'clockify' ? formatTime(r.duration_seconds, true) : r.action || `${r.kind} ${r.version}`}</small></button></li>)}</ol>
        {!filtered.length && <p>No stored records match these filters.</p>}
        {filtered.length > limit && <button type="button" onClick={() => setLimit(n => n + 100)}>Show 100 more records</button>}
    </>;
}



export default function WorkComparisonLayouts({ layout, filters, refresh, selectedIds = null, summaryData = null }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [day, setDay] = useState(filters.start);
    const [selection, setSelection] = useState(null);
    const [query, setQuery] = useState('');
    const inspectionRequest = useRef(0);
    const summaryOnly = summaryData?.storage === 'daily' && ['daily', 'cards', 'trends', 'weeks', 'panels'].includes(layout);
    useEffect(() => { inspectionRequest.current += 1; if (layout !== 'weekly') setEnabled(true); setSelection(null); }, [layout]);
    useEffect(() => { inspectionRequest.current += 1; setSelection(null); }, [selectedIds]);
    useEffect(() => () => { inspectionRequest.current += 1; }, []);
    useEffect(

    () => {
        inspectionRequest.current += 1;
        if (!enabled) return undefined;
        if (summaryOnly) {
            setData(summaryData); setError(''); setSelection(null);
            return undefined;
        }
        let active = true;
        setData(null); setError(''); setSelection(null); setDay(filters.start);
        api.get('/admin/work-comparison', { params: { ...filters, include_records: '1' } }).then(({ data: result }) => {
            if (!active) return;
            setData(result);
        }).catch(() => { if (active) setError('The source records could not be loaded. Refresh to try again.'); });
        return () => { active = false; };
    }, [enabled, filters, refresh, summaryOnly, summaryData]);
    const allPeople = useMemo(() => buildPeople(data), [data]);
    const people = useMemo(() => allPeople.filter(p => selectedIds === null || selectedIds.includes(p.id)), [allPeople, selectedIds]);
    const records = useMemo(() => allRecords(people), [people]);
     
    const inspectSummary = (person, key = null, source = null) => {
        const current = ++inspectionRequest.current;
        const title = `${person.name} · ${key ? dateLabel(key) : `${dateLabel(data.start)} – ${dateLabel(data.end)}`}`;
        setSelection({ title, records: [], loading: true });
        const { view, ...range } = filters;
        api.get('/admin/work-comparison', { params: { ...range, employee_id: person.id,
            ...(key ? { start: key, end: key } : {}) } }).then(({ data: result }) => {
            if (current !== inspectionRequest.current) return;
            const detail = allRecords([{ id: person.id, name: person.name, sources: result.sources }]);
            setSelection({ title, records: detail.filter(record => !source || record.source === source), states: source ? { [source]: result.sources[source] } : result.sources });
        }).catch(() => {
            if (current === inspectionRequest.current) setSelection({ title, records: [], error: 'The selected records could not be loaded.' });
        });
    };
    const maxima = Object.fromEntries(SOURCES.map(s => [s.key, Math.max(1, ...allPeople.flatMap(p => p.days.map(d => d.sources[s.key].value || 0)))]));
    const onRecord = 

    record => setSelection({ key: record.key, title: `${record.personName} · ${sourceName(record.source)}`, records: [record] });
    const onPerson = 

    p => summaryOnly ? inspectSummary(p) : setSelection({ title: `${p.name} · ${dateLabel(data.start)} – ${dateLabel(data.end)}`, states: p.sources, records: records.filter(r => r.personId === p.id) });
    const onDay = 

    (p, key, source) => summaryOnly ? inspectSummary(p, key, source) : setSelection({ key: `${p.id}:${key}${source ? ':' + source : ''}`, title: `${p.name} · ${dateLabel(key)}${source ? ' · ' + sourceName(source) : ''}`, states: source ? { [source]: p.days.find(d => d.key === key)?.sources[source] } : p.days.find(d => d.key === key)?.sources, records: records.filter(r => r.personId === p.id && dayKey(r.at, data.timezone) === key && (!source || r.source === source)) });
    if (layout === 'weekly') return null;
    if (error) return <p role="alert">{error}</p>;
    if (!data) return <p role="status">Loading layout records…</p>;
    if (!people.length) return <p>{data.team?.length ? 'Select people above to compare.' : filters.role === 'attorneys' ? 'No active attorneys.' : 'No active employees.'}</p>;
    return <div className="work-layout" data-layout={layout}>
        <header className="wl-heading"><h3>{LAYOUTS.find(l => l.id === layout)?.label}</h3><div className="wl-legend">{SOURCES.map(s => <span className={`wl-${s.key}`} key={s.key}>{s.icon} {s.label}</span>)}</div></header>
        <RecordInspector selection={selection} onClose={() => { inspectionRequest.current += 1; setSelection(null); }} zone={data.timezone} />
        {layout === 'daily' && <DailyGrid people={people} onDay={onDay} selectedKey={selection?.key} />}
        {layout === 'timeline' && <>
            <div className="wl-day-picker" role="group" aria-label="Timeline day">{people[0].days.map(d => <button type="button" key={d.key} aria-pressed={day === d.key} onClick={() => { setDay(d.key); setSelection(null); }} className={d.isFuture ? 'is-future' : ''}><span>{moment.utc(d.key).format('ddd')}</span><strong>{moment.utc(d.key).format('MMM D')}</strong>{d.isToday && <small>Today</small>}</button>)}<span className="wl-zone">{data.timezone}</span></div>
            {people.map(person => <section key={person.id} aria-label={`${person.name} timeline`}><Timelines person={person} data={data} records={records} day={day} onRecord={onRecord} selectedKey={selection?.key} /></section>)}
        </>}
        {layout === 'cards' && <div className="wl-cards">{people.map(p => <article key={p.id}><button type="button" className="wl-name" onClick={() => onPerson(p)}>{p.name} ↗</button><Totals person={p} />{SOURCES.map(s => <div className="wl-card-chart" key={s.key}><span className={`wl-${s.key}`}>{s.label}</span><Spark person={p} source={s.key} maximum={maxima[s.key]} bars onDay={onDay} /></div>)}<small>{dateLabel(data.start)} – {dateLabel(data.end)}</small></article>)}</div>}
        {layout === 'trends' && <div className="wl-scroll"><table className="wl-trends"><thead><tr><th>Person</th>{SOURCES.map(s => <th key={s.key}>{s.label}</th>)}</tr></thead><tbody>{people.map(p => <tr key={p.id}><th><button type="button" onClick={() => onPerson(p)}>{p.name}</button></th>{SOURCES.map(s => <td key={s.key}><Metric source={s.key} state={p.sources[s.key]} /><Spark person={p} source={s.key} maximum={maxima[s.key]} onDay={onDay} /></td>)}</tr>)}</tbody></table></div>}
        {layout === 'calendar' && <Calendar people={people} records={records} data={data} onRecord={onRecord} />}
        {layout === 'matters' && <label className="wl-control">Find matter or project<input type="search" value={query} onChange={e => setQuery(e.target.value)} /></label>}
        {layout === 'matters' && people.map(person => {
            const groupRecords = records.filter(r => r.personId === person.id);
            return <section key={person.id} aria-label={`${person.name} matters and projects`}><h4>{person.name}</h4>
            <Totals person={person} /><p className="wl-note">Explicit matter links and Clockify project IDs only. Projects and Trello cards without a matter link remain separate; names are not matched.</p>
            <div className="wl-scroll"><table className="wl-matters"><thead><tr><th>Matter / project</th>{SOURCES.map(s => <th key={s.key}>{s.label}</th>)}</tr></thead><tbody>{groupMatters(groupRecords).filter(g => g.label.toLowerCase().includes(query.toLowerCase())).map(group => <tr key={group.key}><th><button type="button" onClick={() => setSelection({ title: `${person.name} · ${group.label}`, records: group.records, states: person.sources })}>{group.label} ›</button></th>{SOURCES.map(s => <td key={s.key}>{isAvailable(person.sources[s.key]) && !group.records.some(r => r.source === s.key) ? <span className="wl-muted">No linked records</span> : <Metric source={s.key} state={person.sources[s.key]} value={sourceTotal(s.key, group.records.filter(r => r.source === s.key))} />}</td>)}</tr>)}</tbody></table></div>
            {!groupMatters(groupRecords).filter(g => g.label.toLowerCase().includes(query.toLowerCase())).length && <p>No stored records match this selection.</p>}
        </section>; })}
        {layout === 'weeks' && <CompareWeeks key={`${data.start}:${data.end}:${data.timezone}`} people={people} data={data} onPerson={onPerson} />}
        {layout === 'feed' && <Feed key={`${data.start}:${data.end}:${data.timezone}`} records={records} people={people} data={data} onRecord={onRecord} />}
        {layout === 'panels' && <div className="wl-panels" style={{ gridTemplateColumns: `repeat(${Math.max(1, people.length)}, minmax(240px, 1fr))` }}>{people.map(p => <article key={p.id}><h3>{p.name}</h3><Totals person={p} />{SOURCES.map(s => <div key={s.key}><h4 className={`wl-${s.key}`}>{s.label}</h4><Spark person={p} source={s.key} maximum={maxima[s.key]} onDay={onDay} /></div>)}{p.days.map(d => <button type="button" className={`wl-panel-day${d.isFuture ? ' is-future' : ''}`} disabled={d.isFuture} key={d.key} onClick={() => onDay(p, d.key)}><span>{dateLabel(d.key)}</span>{SOURCES.map(s => <Metric key={s.key} source={s.key} state={d.sources[s.key]} value={d.sources[s.key].value} />)}</button>)}</article>)}</div>}

    </div>;
}
